import React from 'react';
import { Link } from 'react-router-dom';

function SelfIntroGuide({ darkMode }) {
  const cardClass = darkMode 
    ? 'bg-gray-800 text-white' 
    : 'bg-white text-gray-800';

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-4xl mx-auto">
        
        {/* 헤더 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            자기소개 40초 완벽 가이드
          </h1>
          <p className={`text-xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            40초가 가장 현실적이고 효과적인 자기소개 시간입니다. 
            면접관의 집중력을 유지하면서 핵심만 전달하세요!
          </p>
        </div>

        {/* 40초 자기소개의 장점 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">⭐</span>
            왜 40초가 최적일까요?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="font-bold mb-3 text-green-600">🎯 집중력 유지</h3>
              <p className="text-sm leading-relaxed">
                면접관의 집중력이 가장 높은 시간대입니다. 
                40초 내에 핵심 메시지를 전달하면 강한 인상을 남길 수 있어요.
              </p>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-3 text-blue-600">⚡ 임팩트 있는 전달</h3>
              <p className="text-sm leading-relaxed">
                짧은 시간에 압축된 내용은 더 기억에 남습니다. 
                간결하면서도 강력한 메시지 전달이 가능해요.
              </p>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <h3 className="font-bold mb-3 text-purple-600">🎪 지루함 방지</h3>
              <p className="text-sm leading-relaxed">
                너무 긴 자기소개는 면접관을 지루하게 만듭니다. 
                40초는 듣기에 부담스럽지 않은 적절한 시간이에요.
              </p>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-pink-900 bg-opacity-30' : 'bg-pink-50'}`}>
              <h3 className="font-bold mb-3 text-pink-600">🎯 현실적 시간</h3>
              <p className="text-sm leading-relaxed">
                실제 면접에서 가장 많이 요구되는 시간입니다. 
                현실적이고 실용적인 자기소개 훈련이 가능해요.
              </p>
            </div>
          </div>
        </div>

        {/* 40초 자기소개 황금 구조 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🏗️</span>
            40초 자기소개 황금 구조
          </h2>
          
          <div className="space-y-6">
            {/* 1단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-blue-500 ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</div>
                <h3 className="font-bold text-blue-600">[10초] 인사 + 강점 2가지</h3>
              </div>
              <p className="text-sm leading-relaxed">
                정중한 인사와 함께 자신의 핵심 강점 2가지를 명확히 제시합니다.
                <br/>💡 <strong>포인트:</strong> 인성 강점 + 직무 강점으로 구성하세요!
              </p>
            </div>

            {/* 2단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-green-500 ${darkMode ? 'bg-green-900 bg-opacity-20' : 'bg-green-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</div>
                <h3 className="font-bold text-green-600">[20초] 첫 번째 강점 구체적 경험</h3>
              </div>
              <p className="text-sm leading-relaxed">
                첫 번째 강점(주로 직무 강점)을 뒷받침하는 구체적인 경험과 성과를 제시합니다.
                <br/>💡 <strong>포인트:</strong> 수치나 구체적 방법을 포함하세요!
              </p>
            </div>

            {/* 3단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-purple-500 ${darkMode ? 'bg-purple-900 bg-opacity-20' : 'bg-purple-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
                <h3 className="font-bold text-purple-600">[30초] 두 번째 강점 구체적 경험</h3>
              </div>
              <p className="text-sm leading-relaxed">
                두 번째 강점(주로 인성 강점)을 보여주는 경험을 간결하게 설명합니다.
                <br/>💡 <strong>포인트:</strong> 팀워크나 소통 능력을 구체적으로 보여주세요!
              </p>
            </div>

            {/* 4단계 */}
            <div className={`p-6 rounded-xl border-l-4 border-red-500 ${darkMode ? 'bg-red-900 bg-opacity-20' : 'bg-red-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</div>
                <h3 className="font-bold text-red-600">[40초] 회사 기여 + 감사 인사</h3>
              </div>
              <p className="text-sm leading-relaxed">
                앞서 언급한 강점들로 회사에 어떻게 기여할지 명시하고 정중하게 마무리합니다.
                <br/>💡 <strong>포인트:</strong> 구체적인 기여 방법을 제시하고 "감사합니다"로 마무리!
              </p>
            </div>
          </div>
        </div>

        {/* 완성 예시 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🎭</span>
            완성된 40초 자기소개 예시
          </h2>
          
          <div className={`p-6 rounded-xl ${darkMode ? 'bg-gradient-to-r from-blue-900 to-purple-900 bg-opacity-30' : 'bg-gradient-to-r from-blue-50 to-purple-50'} border-l-4 border-yellow-500`}>
            <p className="text-sm leading-relaxed">
              <span className="font-semibold text-blue-600">[10초]</span> 
              "안녕하십니까! 마케팅 직무에 지원한 김○○입니다. 저의 강점은 데이터 분석 능력과 소통 능력입니다."
              <br/><br/>
              <span className="font-semibold text-green-600">[20초]</span> 
              "첫 번째는 데이터분석 능력입니다. 대학 시절 소상공인 마케팅 프로젝트를 진행했습니다. Google Analytics로 고객 구매 패턴을 분석하였고, 이를 타겟 광고에 적용하며 3개월 만에 매출이 35% 증가하는 성과를 이뤘습니다."(더 상세하게!)
              <br/><br/>
              <span className="font-semibold text-purple-600">[30초]</span> 
              "두 번째는 소통 능력입니다. 제가 생각하는 소통이란 말을 잘하는것 보다는 상대를 존중하며 경청하는 것입니다. 팀 프로젝트에서 전략 방향을 놓고 의견이 나뉜적이 있었습니다. 저는 각 팀원과 개별 면담을 했습니다. 서로 입장을 듣고 데이터를 바탕으로 합의점을 찾았습니다. 결국 프로젝트를 성공적으로 마무리했습니다."
              <br/><br/>
              <span className="font-semibold text-red-600">[40초]</span> 
              "○○기업에서도 이 두 강점을 활용해서 고객 만족과 매출 성장에 기여하겠습니다. 감사합니다."
            </p>
          </div>
        </div>

        {/* 피해야 할 실수들 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">⚠️</span>
            피해야 할 실수들
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'} border-l-4 border-red-500`}>
              <h3 className="font-bold mb-3 text-red-600">❌ 하지 말아야 할 것들</h3>
              <ul className="text-sm space-y-2">
                <li>• <strong>너무 길게 말하기</strong> - 1분 이상 하면 지루해해요</li>
                <li>• <strong>생년월일, 거주지</strong> - 이미 이력서에 있어요</li>
                <li>• <strong>가족사 소개</strong> - 직무와 관련 없는 내용</li>
                <li>• <strong>추상적 표현</strong> - "성실하다", "열정적이다" 등</li>
                <li>• <strong>긴 감사 인사</strong> - "귀한 시간 내주셔서..." 는 불필요</li>
              </ul>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'} border-l-4 border-green-500`}>
              <h3 className="font-bold mb-3 text-green-600">✅ 반드시 포함할 것들</h3>
              <ul className="text-sm space-y-2">
                <li>• <strong>구체적인 성과</strong> - 수치, 결과, 방법론</li>
                <li>• <strong>직무 연관성</strong> - 지원 직무와 연결되는 경험</li>
                <li>• <strong>강점 입증 사례</strong> - 말로만이 아닌 경험으로</li>
                <li>• <strong>회사 기여 방안</strong> - 내가 어떤 도움이 될지</li>
                <li>• <strong>정중한 마무리</strong> - 간단한 "감사합니다"</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 연습 가이드 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🎯</span>
            40초 자기소개 연습 가이드
          </h2>
          
          <div className="space-y-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-3 text-blue-600">1️⃣ 스크립트 작성</h3>
              <p className="text-sm leading-relaxed mb-4">
                위의 구조에 맞춰 자신만의 40초 자기소개를 작성해보세요.
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-blue-600">체크포인트:</p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• 강점 2가지가 명확한가?</li>
                  <li>• 각 강점마다 구체적 사례가 있는가?</li>
                  <li>• 회사 기여 방안이 포함되었는가?</li>
                </ul>
              </div>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="font-bold mb-3 text-green-600">2️⃣ 시간 측정 연습</h3>
              <p className="text-sm leading-relaxed mb-4">
                스마트폰 타이머를 이용해 정확히 40초에 맞춰 연습하세요.
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-green-600">연습 방법:</p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• 10번 연속 40초 맞추기</li>
                  <li>• 각 단계별 10초씩 체크하기</li>
                  <li>• 녹음해서 들어보기</li>
                </ul>
              </div>
            </div>
            
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <h3 className="font-bold mb-3 text-purple-600">3️⃣ 자연스러운 말투 연습</h3>
              <p className="text-sm leading-relaxed mb-4">
                암기한 티가 나지 않도록 자연스럽게 말하는 연습을 하세요.
              </p>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="text-sm font-semibold text-purple-600">포인트:</p>
                <ul className="text-sm mt-2 space-y-1">
                  <li>• 적절한 속도로 또박또박</li>
                  <li>• 중요한 부분에서 강조하기</li>
                  <li>• 시선 처리와 표정 관리</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 뒤로가기 */}
        <div className="text-center">
          <Link 
            to="/guide-success" 
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            ← 면접성공가이드로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}

export default SelfIntroGuide;